import { ref } from 'vue'

/**
 * One app-styled message dialog, the "Time submitted successfully" kind. It
 * replaces the vue-final-modal instance every page used to carry. App.vue
 * renders the single modal-spinner that shows it. Everything is rendered as
 * plain text.
 */
export interface MessageRequest {
  title?: string
  /** The message. Plain text; "\n" breaks a line. */
  message: string
  /** Shown on the line under the message. */
  detail?: string
  /** Close on its own after this many milliseconds; 0 keeps it open until dismissed. */
  autoClose?: number
}

const DEFAULT_TITLE = 'XGuard - Time Entry'

const visible = ref(false)
const title = ref(DEFAULT_TITLE)
const message = ref('')
const detail = ref('')

let timer: ReturnType<typeof setTimeout> | null = null
let settle: (() => void) | null = null

function close() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  visible.value = false
  settle?.()
  settle = null
}

/** Shows the message and resolves when it closes, by the user or by the timer. */
export function showMessage(request: MessageRequest): Promise<void> {
  close()

  title.value = request.title ?? DEFAULT_TITLE
  message.value = request.message
  detail.value = request.detail ?? ''
  visible.value = true

  const autoClose = request.autoClose ?? 4000
  if (autoClose > 0) {
    timer = setTimeout(close, autoClose)
  }

  return new Promise<void>((resolve) => {
    settle = resolve
  })
}

/** For App.vue, which renders the dialog. */
export function useMessageDialog() {
  return { visible, title, message, detail, close }
}
